import { useId, useMemo, type ReactElement } from "react";
import { Sparkles } from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Line,
  LineChart,
  ResponsiveContainer,
} from "recharts";
import type { KpiStatus } from "@playbookai/types";
import { cn } from "../../lib/utils.js";
import { statusToken } from "../../lib/types.js";
import { useReducedMotion } from "../../hooks/useReducedMotion.js";
import { Sparkline } from "../../atoms/Sparkline/Sparkline.js";
import { TrendIndicator } from "../../atoms/TrendIndicator/TrendIndicator.js";
import { statusToSparkTone } from "../kpi-spark-tone.js";
import type { WidgetBodyProps } from "../types.js";
import type { KpiChartView } from "./types.js";

/**
 * Shared body for `singleKpi` and `chart` — they differ only in their default
 * `chartType` + dashboard meta. `line`/`area`/`bar` render via recharts (the
 * DS norm, gated on reduced-motion for VR determinism); the number-centric
 * types render with the static `atoms/Sparkline` (or no chart for `mini-stat`).
 * Data-blind: the host resolves `kpiId → KpiDef` and hands it in as `data.kpi`.
 */

const STATUS_CHART_COLOR: Record<KpiStatus, string> = {
  healthy: "var(--color-pb-green)",
  watch: "hsl(var(--pb-amber))",
  critical: "hsl(var(--pb-red))",
  none: "hsl(var(--pb-border-strong))",
};

const CHART_MARGIN = { top: 4, right: 2, bottom: 0, left: 2 } as const;

export function KpiChartBody({
  data,
}: WidgetBodyProps<KpiChartView>): ReactElement {
  const reducedMotion = useReducedMotion();
  const {
    kpi,
    chartType = "line",
    showDelta = true,
    showCommentary = true,
  } = data;
  const token = statusToken(kpi.status);
  const sparkTone = statusToSparkTone(kpi.status);
  const chartColor = STATUS_CHART_COLOR[kpi.status];
  const gradientId = useId();
  const isRecharts =
    chartType === "line" || chartType === "area" || chartType === "bar";
  const chartData = useMemo(
    () => kpi.series.map((v, i) => ({ i, v })),
    [kpi.series],
  );
  // The oracle renders percentage deltas in "pp" (percentage points).
  const deltaUnit = kpi.unit === "%" ? "pp" : (kpi.unit ?? "");
  const valueSize =
    chartType === "big-number"
      ? "text-3xl"
      : chartType === "mini-stat"
        ? "text-3xl"
        : "text-2xl";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span
          className={cn(
            valueSize,
            "font-mono text-pb-text pb-tabular leading-none",
          )}
        >
          {kpi.value}
        </span>
        {showDelta ? (
          <TrendIndicator
            delta={kpi.deltaNumeric}
            unit={deltaUnit}
            inverse={kpi.inverse}
          />
        ) : null}
      </div>

      {chartType === "mini-stat" ? null : isRecharts ? (
        // Decorative: the value/delta/status/threshold already convey the data
        // textually, so hide the chart graphic from the a11y tree.
        <div className="h-14" aria-hidden="true">
          {/* Kept hand-built: recharts chart marks, which no design-system part draws. */}
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "bar" ? (
              <BarChart data={chartData} margin={CHART_MARGIN}>
                <Bar
                  dataKey="v"
                  fill={chartColor}
                  radius={[2, 2, 0, 0]}
                  isAnimationActive={!reducedMotion}
                />
              </BarChart>
            ) : chartType === "area" ? (
              <AreaChart data={chartData} margin={CHART_MARGIN}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={chartColor}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="100%"
                      stopColor={chartColor}
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke={chartColor}
                  strokeWidth={2}
                  fill={`url(#${gradientId})`}
                  dot={false}
                  isAnimationActive={!reducedMotion}
                />
              </AreaChart>
            ) : (
              <LineChart data={chartData} margin={CHART_MARGIN}>
                <Line
                  type="monotone"
                  dataKey="v"
                  stroke={chartColor}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={!reducedMotion}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      ) : (
        <Sparkline
          data={kpi.series}
          width={240}
          height={chartType === "trend" ? 56 : 42}
          tone={sparkTone}
          fill
          className="w-full"
          aria-hidden="true"
        />
      )}

      <div className="flex items-center justify-between text-dense-1">
        <span className="flex items-center gap-1.5">
          <span className={cn("w-2 h-2 rounded-full", token.dotClass)} />
          <span className="text-pb-text-secondary">{token.label}</span>
        </span>
        <span className="text-pb-text-muted pb-tabular">{kpi.threshold}</span>
      </div>

      {showCommentary && kpi.commentary ? (
        <div className="flex items-start gap-2 border-t border-pb-border/60 pt-3">
          <Sparkles className="w-3.5 h-3.5 text-pb-purple-text mt-0.5 shrink-0" />
          <p className="text-dense-1 text-pb-text-secondary leading-snug">
            {kpi.commentary}
          </p>
        </div>
      ) : null}
    </div>
  );
}
